// alignmentUi.tsx
// Shared look for the Alignment Board: the player sign up form, the member view
// and the officer players page. Spec: md/CLASS_ALIGNMENT_PLAN.md
//
// One module so the three pages cannot drift apart visually. They were built
// separately and ended up as three slightly different grey panels; the sign up
// form was then redesigned and the officer page immediately looked like a
// different product.
//
// Type: Inter throughout. Headings use it at 800 with tight tracking so they
// still stand apart from body text. Cinzel was tried for headings and dropped.
// Both faces are loaded once in public/index.html and applied INLINE through
// these style objects, so pages outside the alignment board keep their font.
import React, { useState } from "react";
import { useTheme } from "./theme/ThemeProvider";

// Class, role and faction colours are authored for dark backgrounds and some
// vanish on the light palette. The top component of each alignment page calls
// useAlignmentTheme() once per render; tc() then darkens a colour just enough
// to read on light, and is identity on dark. See ThemeProvider.
let legible: (hex: string) => string = (hex) => hex;
export function useAlignmentTheme() {
  legible = useTheme().onLight;
}
export function tc<T extends string | null | undefined>(color: T): T {
  return (color ? legible(color) : color) as T;
}

export const DISPLAY: React.CSSProperties = {
  fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.02em",
};
export const BODY: React.CSSProperties = {
  fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
};

export const ROLE_COLOR: Record<string, string> = {
  tank: "#38bdf8",
  healer: "#34d399",
  melee_dps: "#fb923c",
  ranged_dps: "#fb923c",
};

// Brighter than the catalogue's faction colours, because these sit on dark and
// often busy backgrounds and the catalogue values were picked for flat panels.
export const CHOICE_COLOR: Record<string, string> = {
  alliance: "#5AA8FF",
  horde: "#FF5A4E",
  pve: "#34d399",
  pvp: "#fb923c",
  either: "#cbd5e1",
};

export function Glass({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`relative rounded-3xl border border-[var(--c-line)] bg-[color-mix(in_srgb,var(--c-surface)_80%,transparent)] shadow-[var(--c-shadow-lg)] backdrop-blur-xl ${className}`}
    >
      {/* A hairline highlight on the top edge is what makes glass read as a
          surface rather than a dark rectangle. */}
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[var(--c-ink-4)] to-transparent" />
      {children}
    </div>
  );
}

export function PrimaryButton({
  children, className = "", ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      // Light: the palette accent, flat. Dark: the original amber gradient + glow.
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--c-accent)] px-6 py-3.5 text-[15px] font-bold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)] transition hover:bg-[var(--c-accent-hover)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500 dark:shadow-[0_10px_30px_-10px_rgba(245,158,11,0.8)] dark:hover:from-amber-200 dark:hover:to-amber-400 active:translate-y-px disabled:cursor-not-allowed disabled:bg-none disabled:bg-[var(--c-sunken-2)] disabled:text-[var(--c-ink-3)] disabled:shadow-none ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children, className = "", ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-4 py-2.5 text-sm font-semibold text-[var(--c-ink)] transition hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken-2)] active:translate-y-px disabled:opacity-40 ${className}`}
    >
      {children}
    </button>
  );
}

// A class or spec icon with a graceful fallback, because a broken image tile is
// the fastest way to make a page look unfinished.
export function Icon({
  src, alt, size = 40, ring,
}: {
  src: string | null | undefined;
  alt: string;
  size?: number;
  ring?: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const style: React.CSSProperties = {
    width: size,
    height: size,
    boxShadow: ring ? `0 0 0 2px ${ring}, 0 4px 14px -4px ${ring}` : "0 0 0 1px rgba(255,255,255,0.12)",
  };
  if (!src || failed) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-lg bg-[var(--c-sunken-2)] text-sm font-bold text-[var(--c-ink-2)]"
        style={style}
      >
        {alt.slice(0, 1)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="shrink-0 rounded-lg object-cover"
      style={style}
      draggable={false}
    />
  );
}

export function RolePill({ roleKey, label }: { roleKey: string | null; label: string }) {
  const c = ROLE_COLOR[roleKey || ""] || "#94a3b8";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
      style={{ color: tc(c), background: `${c}1f` }}
    >
      {label}
    </span>
  );
}

// A tug of war, not two bars side by side. The two sides push in from the ends
// and the undecided sit in the middle, so "have we actually settled this" reads
// instantly instead of needing three numbers compared.
export function TugOfWar({
  title, left, right, middle, unanswered,
}: {
  title: string;
  left: { label: string; n: number; color: string };
  right: { label: string; n: number; color: string };
  middle: { label: string; n: number };
  unanswered: number;
}) {
  const total = left.n + right.n + middle.n;
  const pct = (n: number) => (total ? (n / total) * 100 : 0);
  const gap = Math.abs(left.n - right.n);
  const verdict = total === 0
    ? "Nobody has said yet"
    : gap === 0
      ? "Dead even"
      : `${left.n > right.n ? left.label : right.label} leads by ${gap}`;

  const seg = (c: string) => ({
    background: `linear-gradient(180deg, ${c}55 0%, ${c}22 100%)`,
    boxShadow: `inset 0 0 20px ${c}33`,
  });

  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
          {title}
        </span>
        <span className="text-xs font-medium text-[var(--c-ink-2)]">{verdict}</span>
      </div>

      <div className="mb-2 flex items-end justify-between">
        <div>
          <div className="text-2xl font-black leading-none" style={{ color: tc(left.color) }}>{left.n}</div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: tc(left.color) }}>
            {left.label}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm font-bold leading-none text-[var(--c-ink-3)]">{middle.n}</div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--c-ink-4)]">{middle.label}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black leading-none" style={{ color: tc(right.color) }}>{right.n}</div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: tc(right.color) }}>
            {right.label}
          </div>
        </div>
      </div>

      <div className="flex h-3 overflow-hidden rounded-full border border-[var(--c-line)] bg-[var(--c-sunken-2)]">
        {total === 0 ? (
          <div className="w-full" />
        ) : (
          <>
            <div style={{ width: `${pct(left.n)}%`, ...seg(left.color) }} />
            <div style={{ width: `${pct(middle.n)}%`, ...seg("#64748b") }} />
            <div style={{ width: `${pct(right.n)}%`, ...seg(right.color) }} />
          </>
        )}
      </div>

      {unanswered > 0 && (
        <div className="mt-2 text-[11px] text-[var(--c-ink-4)]">
          {unanswered} {unanswered === 1 ? "person has" : "people have"} not answered this yet
        </div>
      )}
    </div>
  );
}

// Coverage panels. The counting rule is the one auth/alignment_overview.js
// explains at length, and it is repeated here rather than invented: a player
// who would tank OR heal belongs in BOTH role counts, so those numbers overlap
// and deliberately do not sum to the number of players.
//
// Two figures per thing, because one on its own lies in a different direction
// each time. COULD PLAY is depth, and answers "can we field another healer".
// FIRST CHOICE is disjoint, sums to the roster, and is what people will
// actually turn up as. "12 could heal, 3 want to" is the line that tells an
// officer where they will have to ask somebody to play something they did not
// pick.
//
// Both are always drawn, the first choice figure as a brighter segment inside
// the could-play bar. The toggle is for when the overlap itself is in the way:
// switching to first choice gives a picture that adds up to the roster, which
// is the one to read when you are asking "what will actually show up".
export type Mode = "all" | "first";

const MODE_NOTE: Record<Mode, string> = {
  all: "Counted per player, so anyone who listed two is in both.",
  first: "Top pick only, so this adds up to the whole roster.",
};

function ModeToggle({
  mode, onChange, className = "",
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
  className?: string;
}) {
  return (
    <div className={`flex rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-0.5 ${className}`}>
      {([["all", "Could play"], ["first", "First choice"]] as const).map(([m, label]) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`flex-1 whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-bold transition ${
            mode === m
              ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)]"
              : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// One bar, drawn the same way everywhere: the could-play length in a muted
// wash, with the first choice length brighter inside it. In first choice mode
// there is only the bright one, because the muted length is exactly the thing
// being filtered out.
function CoverageBar({
  can, wants, max, color, mode, rounded = "rounded-lg", className = "",
}: {
  can: number; wants: number; max: number; color: string; mode: Mode;
  rounded?: string; className?: string;
}) {
  return (
    <div className={`relative overflow-hidden border border-[var(--c-line)] bg-[var(--c-sunken-2)] ${rounded} ${className}`}>
      {mode === "all" && (
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${(can / max) * 100}%`,
            background: `linear-gradient(180deg, ${color}55 0%, ${color}22 100%)`,
            boxShadow: `inset 0 0 20px ${color}33`,
          }}
        />
      )}
      <div
        className="absolute inset-y-0 left-0"
        style={{
          width: `${(wants / max) * 100}%`,
          background: `linear-gradient(180deg, ${color}bb 0%, ${color}66 100%)`,
        }}
      />
    </div>
  );
}

export function RoleCoverage({
  roles, total,
}: {
  roles: { key: string; label: string; color: string; can: number; wants: number }[];
  total: number;
}) {
  const [mode, setMode] = useState<Mode>("all");
  const value = (r: { can: number; wants: number }) => (mode === "first" ? r.wants : r.can);
  // Bars are scaled to the deepest role, not to the roster, so the shape of the
  // guild reads at a glance even when nobody is close to a full raid.
  const max = Math.max(1, ...roles.map(value));

  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
          Role coverage
        </span>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs font-medium text-[var(--c-ink-3)] sm:inline">
            {MODE_NOTE[mode]}
          </span>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      <div className="space-y-2.5">
        {roles.map((r) => {
          const c = r.color || "#94a3b8";
          return (
            <div key={r.key} className="grid grid-cols-[108px_minmax(0,1fr)_112px] items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: tc(c) }}>
                {r.label}
              </span>
              <CoverageBar can={r.can} wants={r.wants} max={max} color={c} mode={mode} className="h-7" />
              <div className="text-right leading-none">
                <span className="text-xl font-black" style={{ color: tc(c) }}>{value(r)}</span>
                <span className="ml-1 text-[11px] font-medium text-[var(--c-ink-3)]">of {total}</span>
                <div className="mt-1 text-[11px] font-medium text-[var(--c-ink-3)]">
                  {mode === "first" ? `${r.can} could play` : `${r.wants} first choice`}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {mode === "all" && (
        <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-[var(--c-ink-3)]">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm" style={{ background: "rgba(255,255,255,0.65)" }} />
            Would play it first
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm" style={{ background: "rgba(255,255,255,0.22)" }} />
            Would play it at all
          </span>
        </div>
      )}
    </div>
  );
}

// Class and spec depth, one card per class. Same counting rule and same toggle.
//
// Classes NOBODY picked are not given a card. Twelve cards, four of them zeros,
// buries the eight that matter; the empty ones are named in one line underneath
// instead, which is the form the question actually takes ("nobody rolled a
// paladin"). In first choice mode that filter tightens to nobody's top pick,
// which is the honest reading of the same sentence.
export function ClassCoverage({
  classes, total,
}: {
  classes: {
    key: string; label: string; color: string; icon: string | null;
    can: number; wants: number;
    specs: { key: string; label: string; color: string; icon: string | null; can: number; wants: number }[];
  }[];
  total: number;
}) {
  const [mode, setMode] = useState<Mode>("all");
  const value = (x: { can: number; wants: number }) => (mode === "first" ? x.wants : x.can);

  const filled = classes.filter((c) => value(c) > 0);
  const empty = classes.filter((c) => value(c) === 0);
  // Every spec bar in the panel shares one scale, so "six resto druids and one
  // boomkin" reads across cards instead of only within one.
  const specMax = Math.max(1, ...classes.flatMap((c) => c.specs.map(value)));

  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
          Class and spec coverage
        </span>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs font-medium text-[var(--c-ink-3)] sm:inline">
            {MODE_NOTE[mode]}
          </span>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      {filled.length === 0 ? (
        <div className="py-6 text-center text-sm text-[var(--c-ink-3)]">
          {mode === "first" ? "Nobody has a first choice yet." : "Nobody has picked a class yet."}
        </div>
      ) : (
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
          {filled.map((c) => (
            <div key={c.key} className="rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-3">
              <div className="flex items-center gap-2.5">
                <Icon src={c.icon} alt={c.label} size={34} ring={c.color} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold" style={{ color: tc(c.color) }}>{c.label}</div>
                  <div className="text-[11px] font-medium text-[var(--c-ink-3)]">
                    {mode === "first" ? `${c.can} could play` : `${c.wants} first choice`}
                  </div>
                </div>
                <div className="text-right leading-none">
                  <span className="text-2xl font-black" style={{ color: tc(c.color) }}>{value(c)}</span>
                  <span className="ml-1 text-[11px] font-medium text-[var(--c-ink-4)]">of {total}</span>
                </div>
              </div>

              <div className="mt-2.5 space-y-1.5 border-t border-[var(--c-line-soft)] pt-2.5">
                {c.specs.filter((s) => value(s) > 0).map((s) => (
                  <div key={s.key} className="grid grid-cols-[18px_minmax(0,1fr)_64px_22px] items-center gap-2">
                    <Icon src={s.icon} alt={s.label} size={18} />
                    <span className="truncate text-[12px] font-medium text-[var(--c-ink-2)]" title={s.label}>
                      {s.label}
                    </span>
                    <CoverageBar
                      can={s.can} wants={s.wants} max={specMax} color={s.color} mode={mode}
                      rounded="rounded-full" className="h-2.5"
                    />
                    <span className="text-right text-[12px] font-bold text-[var(--c-ink)]">{value(s)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {empty.length > 0 && (
        <div className="mt-3 text-[11px] text-[var(--c-ink-3)]">
          {mode === "first" ? "Nobody picked first: " : "Nobody signed up as: "}
          {empty.map((c, i) => (
            <span key={c.key}>
              {i > 0 && ", "}
              <span style={{ color: `${tc(c.color)}99` }}>{c.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// The same figures as the officer page, sized for a column beside a form and
// ranked so that the form stays the thing you are looking at. A player
// deciding what to roll wants two questions answered, in this order: which
// side is everyone picking, and what is nobody playing.
//
// Progressive disclosure is doing the work here. Roles are four lines and are
// always open. Classes are a long list, so only the deepest few are shown with
// the rest behind a toggle, and a class opens to its specs only when asked.
// Everything visible at rest fits beside the form without competing with it.
//
// ONE mode toggle for the whole column, not one per card as on the officer
// page: at this width two of them would be most of the furniture, and the two
// cards are read together as a single answer to "what should I roll".
export function CoverageSidebar({
  registered, roles, classes, splits,
}: {
  registered: number;
  roles: { key: string; label: string; color: string; can: number; wants: number }[];
  classes: {
    key: string; label: string; color: string; icon: string | null;
    can: number; wants: number;
    specs: { key: string; label: string; color: string; icon: string | null; can: number; wants: number }[];
  }[];
  splits: {
    title: string;
    left: { label: string; n: number; color: string };
    right: { label: string; n: number; color: string };
    middle: { label: string; n: number };
  }[];
}) {
  const [mode, setMode] = useState<Mode>("all");
  const [showAllClasses, setShowAllClasses] = useState(false);
  const [openClass, setOpenClass] = useState<string | null>(null);

  const value = (x: { can: number; wants: number }) => (mode === "first" ? x.wants : x.can);
  const roleMax = Math.max(1, ...roles.map(value));
  const withAny = classes.filter((c) => value(c) > 0);
  const shownClasses = showAllClasses ? withAny : withAny.slice(0, 6);
  const classMax = Math.max(1, ...withAny.map(value));

  // The one line that actually changes what somebody rolls. Only worth saying
  // once there are enough answers for "fewest" to mean anything.
  const thinnest = roles.length > 0 && registered >= 5
    ? roles.reduce((a, b) => (value(b) < value(a) ? b : a))
    : null;

  if (registered === 0) {
    return (
      <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 backdrop-blur">
        <div className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
          The guild so far
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--c-ink-3)]">
          Nobody has answered yet. You are first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 backdrop-blur">
        <div className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
          The guild so far
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-3xl font-black leading-none text-amber-700 dark:text-amber-300" style={DISPLAY}>
            {registered}
          </span>
          <span className="text-[13px] text-[var(--c-ink-3)]">
            {registered === 1 ? "person has answered" : "people have answered"}
          </span>
        </div>

        {/* The two once-per-person questions, as a line each rather than the
            officer page tug of war, which is too tall to sit above the part
            somebody is actually here to read. These are asked once per person,
            so the mode toggle below does not apply to them. */}
        <div className="mt-4 space-y-3">
          {splits.map((s) => {
            const total = s.left.n + s.right.n + s.middle.n;
            const pct = (n: number) => (total ? (n / total) * 100 : 0);
            return (
              <div key={s.title}>
                <div className="flex items-baseline justify-between gap-2 text-[12px]">
                  <span className="font-semibold" style={{ color: tc(s.left.color) }}>
                    {s.left.label} {s.left.n}
                  </span>
                  <span className="font-semibold" style={{ color: tc(s.right.color) }}>
                    {s.right.n} {s.right.label}
                  </span>
                </div>
                <div className="mt-1 flex h-2 overflow-hidden rounded-full border border-[var(--c-line)] bg-[var(--c-sunken-2)]">
                  <div style={{ width: `${pct(s.left.n)}%`, background: `${s.left.color}99` }} />
                  <div style={{ width: `${pct(s.middle.n)}%`, background: "#64748b66" }} />
                  <div style={{ width: `${pct(s.right.n)}%`, background: `${s.right.color}99` }} />
                </div>
                {s.middle.n > 0 && (
                  <div className="mt-1 text-[11px] text-[var(--c-ink-4)]">
                    {s.middle.n} {s.middle.label.toLowerCase()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ModeToggle mode={mode} onChange={setMode} />

      <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 backdrop-blur">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
            Roles
          </span>
          <span className="text-[11px] text-[var(--c-ink-4)]">
            {mode === "first" ? "first choice" : "could play"}
          </span>
        </div>
        <div className="mt-3 space-y-2.5">
          {roles.map((r) => (
            <div key={r.key}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-bold" style={{ color: tc(r.color) }}>{r.label}</span>
                <span className="text-[12px] font-bold text-[var(--c-ink)]">{value(r)}</span>
              </div>
              <CoverageBar
                can={r.can} wants={r.wants} max={roleMax} color={r.color} mode={mode}
                rounded="rounded-full" className="mt-1 h-2"
              />
            </div>
          ))}
        </div>
        {thinnest && (
          <p className="mt-3 border-t border-[var(--c-line-soft)] pt-2.5 text-[12px] leading-relaxed text-amber-800/70 dark:text-amber-200/70">
            Fewest so far: {thinnest.label.toLowerCase()}.
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 backdrop-blur">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
            Classes
          </span>
          <span className="text-[11px] text-[var(--c-ink-4)]">tap for specs</span>
        </div>
        <div className="mt-3 space-y-1">
          {shownClasses.map((c) => {
            const open = openClass === c.key;
            return (
              <div key={c.key}>
                <button
                  type="button"
                  onClick={() => setOpenClass(open ? null : c.key)}
                  className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left transition ${
                    open ? "bg-[var(--c-sunken)]" : "hover:bg-[var(--c-sunken)]"
                  }`}
                >
                  <Icon src={c.icon} alt={c.label} size={20} />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold" style={{ color: tc(c.color) }}>
                    {c.label}
                  </span>
                  <CoverageBar
                    can={c.can} wants={c.wants} max={classMax} color={c.color} mode={mode}
                    rounded="rounded-full" className="h-2 w-14 shrink-0"
                  />
                  <span className="w-5 shrink-0 text-right text-[12px] font-bold text-[var(--c-ink)]">{value(c)}</span>
                </button>
                {open && (
                  <div className="mb-1 ml-8 space-y-1 border-l border-[var(--c-line)] pl-2.5">
                    {c.specs.filter((s) => value(s) > 0).map((s) => (
                      <div key={s.key} className="flex items-center gap-2">
                        <Icon src={s.icon} alt={s.label} size={14} />
                        <span className="min-w-0 flex-1 truncate text-[11px] text-[var(--c-ink-2)]">{s.label}</span>
                        <span className="text-[11px] font-semibold" style={{ color: tc(s.color) }}>{value(s)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {withAny.length > 6 && (
          <button
            type="button"
            onClick={() => setShowAllClasses((v) => !v)}
            className="mt-2 text-[12px] font-semibold text-amber-700/80 dark:text-amber-300/80 transition hover:text-amber-800 dark:hover:text-amber-200"
          >
            {showAllClasses ? "Show fewer" : `Show all ${withAny.length} classes`}
          </button>
        )}

        {classes.length > withAny.length && (
          <p className="mt-2.5 text-[11px] leading-relaxed text-[var(--c-ink-3)]">
            {mode === "first" ? "Nobody picked first: " : "Nobody yet: "}
            {classes.filter((c) => value(c) === 0).map((c, i) => (
              <span key={c.key}>
                {i > 0 && ", "}
                <span style={{ color: `${tc(c.color)}99` }}>{c.label}</span>
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
}
